<script lang="ts" setup>
import { NewsListService, NewsListData } from '@/modules/core/common/services/NewsListService';
import { computed, onMounted, ref } from 'vue';
import { defineProps } from 'vue';
import { RouterLink } from 'vue-router';

const props = defineProps({
  "config": null,
  "widgetConfig": null
});

const newsList = ref<NewsListData>(null);

const series = computed(() => newsList.value ? newsList.value.series : []);

const config = computed(() => props.config);
const licence = computed(() => config.value.moduleId.licence.toLowerCase());
const sitemapId = computed(() => config.value.sitemapEntryDetails.publicId);
const allNewsLink = computed(() => `/module/${licence.value.toLowerCase()}/newslist/${sitemapId.value}`);

onMounted(async () =>
{
    newsList.value = await NewsListService.getNewsList(licence.value, sitemapId.value);
});
</script>

<template>
    <div v-if="series.some(set => set.documents && set.documents.length > 0)">
        <h4 class="title">
            {{ $t('[[[Ostatnio dodane]]]') }}
        </h4>
        <template v-if="series.length > 0">
            <ul v-for="(set, index) in series" :key="index" class="series-list">
                <ul class="series-set">
                    <li v-for="(item, itemIndex) in set.documents" :key="itemIndex" class="series-set-item">
                        <div class="content-wrapper">
                            <h4 class="title">
                                <RouterLink
                                    :to="`/module/${licence}/details/${item.publicId}`"
                                    class="text-decoration-none text-dark"
                                >
                                    {{ item.shortDescription }}
                                </RouterLink>
                            </h4>
                            <ul class="traits">
                                <li v-for="(trait, traitIndex) in item.description" :key="traitIndex" class="trait">
                                    <span class="trait-key">
                                        {{ NewsListService.mapTraitHeaderValue(trait, set) }}:
                                    </span>
                                    <span class="trait-value">
                                        <template v-if="NewsListService.getTraitType(trait, set) == 'Ideo.Logito.App.ApiDateTime'">
                                            {{ $filters.datetime(trait.value.replace(' ', "T") + "Z") }}
                                        </template>
                                        <template v-else>
                                            {{ trait.value }}
                                        </template>
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div v-if="item.photo && item.photo.isImage" class="image-wrapper">
                            <img :src="set.config.photoPreset ? $filters.image(item.photo?.url, set.config.photoPreset as string) : $filters.baseurl(item.photo?.url)" :alt="item.title" class="image">
                        </div>
                    </li>
                </ul>
            </ul>
        </template>
        <template v-else>
            <div class="no-news-message">
                {{ $t('[[[Brak aktualności do wyświetlenia]]]') }}
            </div>
        </template>
        <div v-if="series.length > 0" class="link-wrapper">
            <a :href="allNewsLink" class="link">
                {{ $t('[[[Zobacz wszystkie aktualności]]]') }}
            </a>
        </div>
    </div>
</template>

<style scoped>
.title {
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 16px;
}

.series-list,
.series-set,
.traits {
    list-style: none;
    padding: 0;
}

.series-set {
    display: flex;
    flex-direction: column;
    gap: 16px;

    &:nth-child(even) {
        background-color: rgba(var(--bs-tertiary-bg-rgb), var(--bs-bg-opacity)) !important;
    }

    .series-set-item {
        display: flex;
        gap: 32px;
        padding-bottom: 16px;

        &:not(:last-child) {
            border-bottom: 2px solid var(--bs-border-color);
        }

        .image-wrapper {
            border-radius: 12px;
            height: 100px;
            width: 100%;
            max-width: 100px;

            .image {
                height: 100%;
                width: 100%;
                object-fit: cover;
                border-radius: 8px;
            }
        }

        .content-wrapper {
            display: flex;
            flex-direction: column;
            width: 100%;
            gap: 10px;

            .title {
                font-size: 16px;
                margin-bottom: 0;
            }

            .traits {
                display: flex;
                flex-direction: column;
                gap: 16px;
                font-size: 16px;

                .trait {
                    display: flex;
                    gap: 4px;

                    .trait-key {
                        font-weight: 700;
                    }

                    .trait-value {
                        font-weight: 500;
                    }
                }
            }

            .description {
                font-size: 14px;
                font-weight: 500;
                margin-bottom: 0;
            }
        }
    }
}

.link-wrapper {
    .link {
        font-size: 16px;
        font-weight: 500;
        color: var(--bs-primary);
    }
}

.no-news-message {
    text-align: center;
    padding: 24px;
    font-size: 16px;
    color: var(--bs-secondary);
    background-color: rgba(var(--bs-tertiary-bg-rgb), var(--bs-bg-opacity));
    border-radius: 8px;
    margin: 16px 0;
}
</style>
